import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SharingRidesList } from '../sharing-rides/sharing-rides-list/sharing-rides-list';
import { RentalRides } from '../rental-rides/rental-rides';
import { ReservationList } from '../../reservations/reservation-list/reservation-list';
import { RideService } from '../../../services/rides/ride-service';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';

export type RidesTab = 'RIDE_SHARING' | 'RIDE_RENTAL' | 'RESERVATION';

@Component({
  selector: 'app-rides-list',
  imports: [CommonModule, SharingRidesList, RentalRides, ReservationList, CardModule],
  templateUrl: './rides-list.html',
  styleUrl: './rides-list.scss'
})
export class RidesList implements OnInit {

  activeTab = signal<RidesTab>('RIDE_SHARING');

  sharingCount = signal(0);
  rentalCount = signal(0);
  reservationCount = signal(0);
  activeCount = signal(0);

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private rideService = inject(RideService);
  private messageService = inject(MessageService);

  ngOnInit() {
    this.activatedRoute.queryParamMap.subscribe(params => {
      const tab = (params.get('tab') as RidesTab) ?? 'RIDE_SHARING';
      this.activeTab.set(tab);
    });
    this.loadCounts();
  }

  loadCounts() {
    this.rideService.getRidesCounts().subscribe(res => {
      this.activeCount.set(res.data.activeRides);
      this.sharingCount.set(res.data.totalSharingRides);
      this.rentalCount.set(res.data.totalRentalRides);
      this.reservationCount.set(res.data.totalReservations);
    });
  }

  switchTab(tab: RidesTab) {
    this.activeTab.set(tab);
    const queryParams: Record<string, string | null> = { tab };

    if (tab !== 'RESERVATION') {
      // Preserve search when moving between sharing ↔ rental
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge'
      });
    } else {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams,
      });
    }

  }

  exportRides() {
    const tab = this.activeTab();
    const qp = this.activatedRoute.snapshot.queryParams;

    if (qp['startedFrom'] && qp['startedTo']) {
      const from = new Date(qp['startedFrom']);
      const to = new Date(qp['startedTo']);
      const diffMs = to.getTime() - from.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      if (diffDays > 92) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Date range too large',
          detail: 'Export is limited to a maximum of 3 months. Please narrow your date range.',
          life: 5000,
        });
        return;
      }
    }

    const rentalTypeMap: Partial<Record<RidesTab, string>> = {
      RIDE_SHARING: 'CAR_SHARING',
      RIDE_RENTAL: 'CAR_RENTAL',
    };
    const rentalType = rentalTypeMap[tab];
    const exportParams: Record<string, string> = {};
    if (rentalType) exportParams['rentalType'] = rentalType;
    const forwardKeys = ['status', 'modelId', 'startedFrom', 'startedTo', 'minRating', 'maxRating', 'photoApprovalStatus', 'search', 'sortBy', 'sortDirection', 'page', 'size'];
    for (const key of forwardKeys) {
      if (qp[key] != null) exportParams[key] = qp[key];
    }
    this.rideService.exportRides(exportParams).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rides-export-${tab.toLowerCase()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }
}
